<template>
  <div class="navbar-container">
    <nav class="navbar" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
        <a class="navbar-item" :href="isNavDisabled ? '' : '/' " :target="isNavDisabled ? '' : '_blank'">
          <img v-if="logo" :src="logo" alt="ShareDocView Logo" />
          <img v-else src="@/assets/sdv-white.png" alt="ShareDocView Logo" />
        </a>
        <a
          role="button"
          class="navbar-burger burger"
          aria-label="menu"
          @click="showNav = !showNav"
          :class="{ 'is-active': showNav }"
          aria-expanded="false"
          data-target="navbarSDV"
          v-if="isLogged"
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </a>
      </div>
      <div id="navbarSDV" class="navbar-menu" :class="{ 'is-active': showNav }">
        <div class="navbar-start" v-if="isLogged">
          <router-link
            tag="a"
            to="/dashboard"
            class="navbar-item"
            :class="{ 'is-active': currentPage.includes('dashboard') }"
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Dashboard
          </router-link>
          <router-link
            tag="a"
            to="/content"
            class="navbar-item"
            :class="{
              'is-active': isContentActiveOnMobile
            }"
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Content
          </router-link>
          <router-link
            tag="a"
            to="/leads"
            class="navbar-item"
            :class="{ 'is-active': currentPage.includes('leads') }"
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Leads
          </router-link>
          <router-link
            tag="a"
            to="/content/bookmarks"
            class="navbar-item is-hidden-desktop"
            :class="{
              'is-active': currentPage.includes('content/bookmarks')
            }"
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Bookmarks
          </router-link>
          <router-link
            tag="a"
            :to="{ name: disableIntegrations ? 'upgrade' : 'integrations' }"
            class="navbar-item"
            :class="[
              disableIntegrations ? ['tooltip', 'is-tooltip-bottom'] : '',
              currentPage.includes('integrations') ? 'is-active' : ''
            ]"
            data-tooltip="Please upgrade your plan to gain access."
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Integrations
            <span class="upgrade link" v-if="disableIntegrations">
              <span class="icon">
                <i class="fas fa-crown"></i>
              </span>
            </span>
          </router-link>
          <router-link
            tag="a"
            :to="{ name: disableReport ? 'upgrade' : 'reports' }"
            class="navbar-item"
            :class="[
              disableReport ? ['tooltip', 'is-tooltip-bottom'] : '',
              currentPage.includes('reports') ? 'is-active' : ''
            ]"
            data-tooltip="Please upgrade your plan to gain access."
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Reports
            <span class="upgrade link" v-if="disableReport">
              <span class="icon">
                <i class="fas fa-crown"></i>
              </span>
            </span>
          </router-link>
          <router-link
            tag="a"
            :to="{
              path: disableTeams ? '/upgrade-enterprise' : '/profile/team'
            }"
            class="navbar-item"
            :class="[disableTeams ? ['tooltip', 'is-tooltip-bottom'] : '']"
            data-tooltip="Please contact our sales team in the live chat to gain access."
            @click.native="showNav = !showNav"
            v-if="!isNavDisabled"
          >
            Team
            <span class="icon ml-2" v-if="disableTeams">
              <i class="fas fa-crown"></i>
            </span>
            <span class="ml-2 tag is-primary is-light is-rounded" v-else>
              Beta
            </span>
          </router-link>
          <a
            href="https://chrome.google.com/webstore/detail/sharedocviewcom-get-leads/ogjfimijdladnppnmfjmgmbcgkjddaoo"
            class="navbar-item"
            target="_blank"
            rel="noopener noreferrer nofollow"
            v-if="!isNavDisabled"
          >
            Get the Extension
            <small class="ml-1 link">
              <span class="icon">
                <i class="fas fa-external-link-alt"></i>
              </span>
            </small>
          </a>
        </div>
        <div class="navbar-end" v-if="isLogged">
          <router-link
            tag="a"
            @click.native="clickUpgrade()"
            class="navbar-item button"
            to="/upgrade"
            v-if="showUpgrade"
          >
            <span class="icon">
              <i class="fas fa-hand-point-up"></i>
            </span>
            <span>Upgrade</span>
          </router-link>
          <div
            class="navbar-item has-dropdown is-hoverable"
            :class="{ 'is-active-item': currentPage.includes('profile') }"
            v-if="!isNavDisabled"
          >
            <a class="navbar-link" :class="{ 'has-text-premium': isPremium }">
              {{ name }}
            </a>
            <div class="navbar-dropdown">
              <router-link
                to="/profile"
                tag="a"
                class="navbar-item"
                @click.native="showNav = !showNav"
              >
                Account
              </router-link>
              <router-link
                to="/profile/billing"
                tag="a"
                class="navbar-item"
                @click.native="showNav = !showNav"
              >
                Billing
              </router-link>
              <router-link
                to="/profile/subscription"
                tag="a"
                class="navbar-item"
                @click.native="showNav = !showNav"
              >
                Subscription
              </router-link>
              <router-link
                to="/profile/branding"
                tag="a"
                class="navbar-item"
                @click.native="showNav = !showNav"
              >
                Branding
              </router-link>
              <router-link
                tag="a"
                :to="{
                  path: disableTeams ? '/upgrade-enterprise' : '/profile/team'
                }"
                class="navbar-item"
                :class="[disableTeams ? ['tooltip', 'is-tooltip-bottom'] : '']"
                data-tooltip="Please contact our sales team in the live chat to gain access."
                @click.native="showNav = !showNav"
              >
                Team
                <span class="icon ml-2" v-if="disableTeams">
                  <i class="fas fa-crown"></i>
                </span>
                <span class="ml-2 tag is-primary is-light is-rounded" v-else>
                  Beta
                </span>
              </router-link>
              <router-link
                to="/profile/payment"
                tag="a"
                class="navbar-item"
                @click.native="showNav = !showNav"
              >
                Promo Codes
              </router-link>
              <hr class="my-1" />
              <a @click="logout()" class="navbar-item has-text-warning">
                Logout
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <div class="navbar-line" :style="borderImage"></div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      showNav: false
    };
  },
  computed: {
    isNavDisabled() {
      return this.$store.getters['auth/getNavDisabledStatus'];
    },
    isContentActiveOnMobile() {
      let active = this.currentPage.match(/content|documents/);
      if (
        window.innerWidth < 1024 &&
        this.currentPage.includes('content/bookmarks')
      ) {
        active = false;
      }
      return active;
    },
    currentPage() {
      return this.$route.path;
    },
    hasCustomColor() {
      return Boolean(this.$store.getters['branding/getColor']);
    },
    name() {
      return this.$store.getters['auth/getUser'].name || 'User';
    },
    isLogged() {
      let user = this.$store.getters['auth/getUser'];
      return !!user._id;
    },
    logo() {
      return this.$store.getters['branding/getLogo'];
    },
    primaryColor() {
      return this.$store.getters['branding/getPrimaryColor'];
    },
    secondaryColor() {
      return this.$store.getters['branding/getSecondaryColor'];
    },
    borderImage() {
      if (this.hasCustomColor) {
        return {
          'background-image': `linear-gradient(to right, ${this.primaryColor} 0%, ${this.primaryColor} 100%)`
        };
      } else {
        return {};
      }
    },
    showUpgrade() {
      // The inverse of the premium plan.
      return !this.isPremium;
    },
    isPremium() {
      const user = this.$store.getters['auth/getUser'];
      const plan = user.account.subscription.plan;
      return (
        plan.name === 'Premium' ||
        plan.name.indexOf('God') !== -1 ||
        plan.type === 'enterprise'
      );
    },
    disableReport() {
      let accessLevel = this.$store.getters['auth/getAccessLevel'];
      return (accessLevel && accessLevel.reports?.value !== true) || false;
    },
    disableIntegrations() {
      let accessLevel = this.$store.getters['auth/getAccessLevel'];
      return (accessLevel && accessLevel.zapier?.value !== true) || false;
    },
    disableTeams() {
      let accessLevel = this.$store.getters['auth/getAccessLevel'];
      // The result is inverted.
      return !(parseInt(accessLevel?.users?.value, 10) > 1 || false);
    }
  },
  methods: {
    hideNav() {
      this.showNav = false;
    },
    logout() {
      this.$axios
        .get(this.$API.logout)
        .then(() => {
          this.$store.commit('auth/logout');
          this.$store.commit('documents/logout');
          this.$axios.defaults.headers.common['auth'] = '';
          this.$router.push('/');
        })
        .catch(error => {
          console.error('Error while logging out ', error);
        });
    },
    clickUpgrade() {
      // send customerly event when user tries to upgrade the account.
      this.$customerly.event('click_upgrade');
    }
  },
  mounted() {
    let anchors = this.$el.querySelectorAll('a:not(.burger)');
    if (anchors.length) {
      anchors.forEach(a => {
        a.addEventListener('click', this.hideNav);
      });
    }
  }
};
</script>
